import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 스크롤 밖 열은 앞뒤로 이만큼 더 그린다.
 *
 * 스크롤과 리렌더 사이의 한 프레임 동안 빈 공간이 보이지 않게 하는 여유분이다.
 * 빠르게 드래그하면 한 프레임에 이보다 더 움직일 수 있어 그리는 게 살짝 늦게 따라온다 —
 * 늘리면 여유가 생기지만 매 갱신마다 그리는 열이 함께 늘어나므로, 4주(28열)를 절충점으로 둔다.
 */
const BUFFER_COLUMNS = 28;

type UseVirtualColumnsParams = {
  /** 전체 열 수 (조회 구간의 일 수) */
  totalColumns: number;
  /** 열 하나의 폭(px) */
  columnWidth: number;
  /** 그리드 왼쪽에 고정된 영역(트랙 라벨) 폭 — 그만큼은 열 영역이 아니다 */
  offsetWidth: number;
  /**
   * 스크롤이 일어날 때 호출된다.
   *
   * 가상화는 스크롤 도중 막대를 **DOM 에서 제거**하는데, 그때 `onMouseLeave` 는 오지 않는다.
   * hover 카드처럼 마우스 위에 떠 있는 것들은 이 신호로 직접 닫아야 이전 항목 정보가 남지 않는다.
   */
  onScroll?: () => void;
};

type UseVirtualColumnsReturn = {
  /** 가로 스크롤 컨테이너에 붙일 ref */
  scrollRef: (node: HTMLDivElement | null) => void;
  /** 그릴 첫 열의 0-based 인덱스 */
  startIndex: number;
  /** 그릴 열 수 */
  visibleCount: number;
  /** 그리드를 원래 위치로 밀어주는 값(px) — 앞쪽 열을 안 그리므로 그만큼 띄운다 */
  offsetPx: number;
};

/**
 * 간트 가로축(일자 열) 가상화.
 *
 * 연 단위 조회는 371열이고, 트랙 6개 밴드가 각각 그 그리드를 갖는다. 열 전부를 그리면
 * 그리드 정의(`repeat(371, 34px)`)와 자식 노드가 함께 커져 레이아웃·삽입 비용이 급증한다.
 * **보이는 구간 + 버퍼만** 그리고, 앞쪽에 안 그린 만큼을 `margin-left` 로 밀어 스크롤 위치를 맞춘다.
 * 전체 스크롤 폭은 컨테이너의 `min-width` 가 유지하므로 스크롤바 길이는 그대로다.
 *
 * ⚠️ 스크롤 이벤트마다 setState 하면 리렌더가 스크롤을 따라가지 못한다 →
 * `requestAnimationFrame` 으로 프레임당 한 번만 반영하고, **열 인덱스가 실제로 바뀔 때만** 갱신한다.
 */
export const useVirtualColumns = (params: UseVirtualColumnsParams): UseVirtualColumnsReturn => {
  const { totalColumns, columnWidth, offsetWidth, onScroll } = params;

  /**
   * ⚠️ 초기 `visibleCount` 는 **0** 이다.
   *
   * `totalColumns` 로 두면 mount 첫 커밋에 열 전부(연 단위 371개 × 트랙 6개)를 DOM 에 넣은 뒤
   * ref 콜백의 `measure()` 가 좁히므로, **가상화로 없앤 비용을 mount 마다 다시 낸다.**
   * 호출부가 `isLoading ? <Spin/> : <GanttView/>` 구조라 이 컴포넌트는 항상 데이터를 들고 mount 되고,
   * 간트↔캘린더 전환도 remount 다.
   * ref 콜백의 setState 는 paint 전에 반영되므로 빈 화면이 깜빡이지 않는다.
   */
  const [range, setRange] = useState({ startIndex: 0, visibleCount: 0 });
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  /** 스크롤 리스너가 항상 최신 measure 를 부르도록 — 리스너를 재등록하지 않기 위한 우회 */
  const scheduleMeasureRef = useRef<() => void>(() => {});
  /** 최신 콜백을 참조만 한다 — 의존성에 넣으면 호출부가 매번 새 함수를 줄 때 리스너가 재등록된다 */
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  const measure = useCallback(() => {
    const node = nodeRef.current;

    if (!node) {
      return;
    }

    /**
     * 트랙 라벨은 sticky 로 그리드 **위에 겹쳐** 있다. 그래서
     * - 보이는 폭에서는 라벨이 덮는 만큼을 뺀다.
     * - 스크롤량에서는 빼지 않는다 — 그리드 좌표계의 첫 보이는 열은 `scrollLeft / columnWidth` 다.
     *   여기서 라벨 폭을 빼면 창이 6열쯤 왼쪽으로 밀려 오른쪽 실효 버퍼가 그만큼 줄어든다.
     */
    const viewportWidth = Math.max(node.clientWidth - offsetWidth, 0);
    const firstVisible = Math.floor(Math.max(node.scrollLeft, 0) / columnWidth);
    const visible = Math.ceil(viewportWidth / columnWidth);

    /**
     * 🟡 `startIndex` 에도 상한을 둔다.
     *
     * 조회 단위를 연 → 월로 바꾸면 옛 range 로 그려진 넓은 콘텐츠 때문에 `scrollLeft` 가 아직 큰데
     * `totalColumns` 는 이미 작아진 상태로 measure 가 돈다. 상한이 없으면
     * `startIndex(260) > endIndex(31)` 이 되어 `visibleCount` 가 음수가 되고
     * `repeat(-229, 34px)` 라는 무효 CSS 가 만들어진다.
     */
    const startIndex = Math.min(Math.max(firstVisible - BUFFER_COLUMNS, 0), Math.max(totalColumns - 1, 0));
    const endIndex = Math.min(Math.max(firstVisible + visible + BUFFER_COLUMNS, startIndex), totalColumns);

    setRange((prev) =>
      prev.startIndex === startIndex && prev.visibleCount === endIndex - startIndex
        ? prev
        : { startIndex, visibleCount: endIndex - startIndex },
    );
  }, [columnWidth, offsetWidth, totalColumns]);

  const handleScroll = useCallback(() => {
    onScrollRef.current?.();
    scheduleMeasureRef.current();
  }, []);

  const scheduleMeasure = useCallback(() => {
    if (frameRef.current !== null) {
      return;
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      measure();
    });
  }, [measure]);

  scheduleMeasureRef.current = scheduleMeasure;

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      const previous = nodeRef.current;

      if (previous) {
        previous.removeEventListener('scroll', handleScroll);
      }

      observerRef.current?.disconnect();
      observerRef.current = null;

      nodeRef.current = node;

      if (node) {
        node.addEventListener('scroll', handleScroll, { passive: true });

        /**
         * 창 크기가 그대로여도 컨테이너 폭은 바뀐다 — 사이드바 접기/펴기, 필터 영역 펼침 등.
         * 그때 다시 재지 않으면 넓어진 쪽에 빈 열 구간이 남고 사용자가 스크롤하기 전까지 복구되지 않는다.
         */
        const observer = new ResizeObserver(() => scheduleMeasureRef.current());
        observer.observe(node);
        observerRef.current = observer;

        measure();
      }
    },
    [handleScroll, measure],
  );

  /**
   * 조회 구간이 바뀌면 **스크롤과 범위를 처음으로 되돌린다.**
   *
   * 되돌리지 않으면 옛 `scrollLeft`(연 단위에서 오른쪽 끝) 로 새 구간(월 단위)을 계산해
   * 빈 밴드와 널뛰는 스크롤바가 몇 프레임 보인다. 구간을 바꾼 사용자는 처음부터 보려는 것이므로
   * 왼쪽으로 되돌리는 편이 의도에도 맞다.
   */
  useEffect(() => {
    const node = nodeRef.current;

    if (node) {
      node.scrollLeft = 0;
    }

    setRange({ startIndex: 0, visibleCount: 0 });
    measure();
  }, [measure, totalColumns]);

  // 창 크기 변경 — 보이는 열 수가 달라진다
  useEffect(() => {
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      window.removeEventListener('resize', scheduleMeasure);
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [scheduleMeasure]);

  return {
    scrollRef,
    startIndex: range.startIndex,
    visibleCount: range.visibleCount,
    offsetPx: range.startIndex * columnWidth,
  };
};
